import React, { useEffect, useState } from 'react';
import type { SrmModule, SrmProject } from '@types';

interface ReviewDialogProps {
    onClose: () => void;
    onSubmit: (email: string, notes: string) => void;
    isSubmitting: boolean;
}

export const ReviewDialog: React.FC<ReviewDialogProps> = ({ onClose, onSubmit, isSubmitting }) => {
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="bg-indigo-600 p-6 text-white">
                    <h3 className="text-xl font-bold">Route for Professional Review</h3>
                    <p className="text-indigo-100 text-sm mt-1">Submit this plan to a certified resiliency professional for formal validation.</p>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Reviewer Email Address</label>
                        <input 
                            type="email" 
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800"
                            placeholder="expert@inteloptics.org"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Contextual Notes for Reviewer</label>
                        <textarea 
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none h-32 text-slate-800"
                            placeholder="Add any specific concerns or focus areas..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-slate-600 font-bold hover:text-slate-800 transition-colors">Cancel</button>
                        <button 
                            onClick={() => onSubmit(email, notes)}
                            disabled={!email || isSubmitting}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:bg-slate-300 transition-all shadow-md active:translate-y-0.5"
                        >
                            {isSubmitting ? 'Routing...' : 'Submit for Review'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SrmTargetDialogProps {
    projects: SrmProject[];
    preferredProjectId?: string;
    preferredModuleId?: string;
    error?: string;
    isSaving: boolean;
    onClose: () => void;
    onSubmit: (project: SrmProject, module: SrmModule) => void;
}

export const SrmTargetDialog: React.FC<SrmTargetDialogProps> = ({
    projects,
    preferredProjectId,
    preferredModuleId,
    error,
    isSaving,
    onClose,
    onSubmit,
}) => {
    const preferredProject = projects.find(project => project.id === preferredProjectId);
    const [projectId, setProjectId] = useState(preferredProject?.id || projects[0]?.id || '');
    const selectedProject = projects.find(project => project.id === projectId);
    const preferredModule = selectedProject?.modules.find(module => module.id === preferredModuleId);
    const [moduleId, setModuleId] = useState(preferredModule?.id || selectedProject?.modules[0]?.id || '');

    useEffect(() => {
        if (projects.some(project => project.id === projectId))
            return;
        const project = projects.find(item => item.id === preferredProjectId) || projects[0];
        setProjectId(project?.id || '');
    }, [projectId, preferredProjectId, projects]);

    useEffect(() => {
        const project = projects.find(item => item.id === projectId);
        const module = project?.modules.find(item => item.id === preferredModuleId) || project?.modules[0];
        setModuleId(module?.id || '');
    }, [projectId, preferredModuleId, projects]);

    const selectedModule = selectedProject?.modules.find(module => module.id === moduleId);

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="bg-amber-600 p-6 text-white">
                    <h3 className="text-xl font-bold">Save to SRM</h3>
                    <p className="text-amber-100 text-sm mt-1">Choose a framework and matching report module for this facility.</p>
                </div>
                <div className="p-6 space-y-4">
                    {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
                    {!error && projects.length === 0 && (
                        <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                            No SRM framework exists for this facility.
                        </p>
                    )}
                    {projects.length > 0 && (
                        <>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">SRM Project</label>
                                <select value={projectId} onChange={event => setProjectId(event.target.value)} className="w-full p-3 border rounded-lg text-slate-800">
                                    {projects.map(project => <option key={project.id} value={project.id}>{project.name || project.projectName || 'Unnamed Project'}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Framework Module</label>
                                <select value={moduleId} onChange={event => setModuleId(event.target.value)} className="w-full p-3 border rounded-lg text-slate-800">
                                    {(selectedProject?.modules || []).map(module => <option key={module.id} value={module.id}>{module.title}</option>)}
                                </select>
                            </div>
                        </>
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} disabled={isSaving} className="px-4 py-2 text-slate-600 font-bold hover:text-slate-800 disabled:opacity-50">Cancel</button>
                        <button
                            onClick={() => selectedProject && selectedModule && onSubmit(selectedProject, selectedModule)}
                            disabled={!selectedProject || !selectedModule || isSaving}
                            className="px-6 py-2 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 disabled:bg-slate-300"
                        >
                            {isSaving ? 'Attaching...' : 'Attach Report'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
